/**
 * New text — `docs/activity.md` §9.3, Board 11 B (480).
 *
 * *To* (the contact and the number), **Attached to**, *Message* with the
 * camera, the paperclip and the counter, and the from line. Picking who it is
 * to brings what they are attached to (§9.2). `POST /conversations` finds or
 * makes the customer's conversation, sets its links, makes the sender its
 * owner and queues the message (§7.4).
 */
import { useEffect, useState } from 'react'
import { countText, formatPhone, showsOnWords } from '@rowted/shared'
import { Glyph } from '../glyphs.js'
import { activityRequest } from '../api.js'
import { AttachedTo, CONVERSATION_KINDS, cameWithWords, suggestFor, withChips, type AttachChip } from './attach.js'
import { refsOf, openedFromWords, problemOf, useContact, usePrefillChips, useSetup, type NewPrefill } from './common.js'
import { ContactField } from './contact-field.js'
import { FileChips, FileTools, useFiles } from './files.js'
import { Field, Gap, NewShell, WordsBox } from './kit.js'

export function TextSheet({
  prefill, phone, onClose, onSwitch, onMade,
}: {
  prefill: NewPrefill; phone: boolean; onClose: () => void
  /** *No mobile number for Renee* offers **New email** instead. */
  onSwitch: (kind: 'email', prefill: NewPrefill) => void
  onMade: () => void
}) {
  const setup = useSetup()
  const [contactId, setContactId] = useState<string | null>(prefill.contactId ?? prefill.contact?.id ?? null)
  const contact = useContact(contactId)
  const [chips, setChips] = usePrefillChips(prefill)
  const [picked, setPicked] = useState<{ chip: AttachChip; brought: AttachChip[] } | null>(null)
  const [body, setBody] = useState(prefill.body ?? '')
  const files = useFiles()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const fromThread = Boolean(prefill.fromConversationId)
  const threadName = fromThread ? (contact?.name ?? null) : null
  const hint = cameWithWords(picked) ?? openedFromWords({ name: threadName, kind: 'thread', required: true, phone })
  const count = countText(body)
  const noMobile = contact !== null && !contact.mobile
  const stopped = contact !== null && contact.optedOut

  const pickContact = async (id: string) => {
    setContactId(id)
    const label = await activityRequest<{ name: string }>(`/activity/compose?contactId=${id}`).then((one) => one.name).catch(() => '')
    const chip: AttachChip = { type: 'contact', id, label, auto: false }
    if (chips.some((one) => one.type === 'contact' && one.id === id)) return
    const brought = (await suggestFor('contact', id)).filter((one) => !chips.some((had) => had.type === one.type && had.id === one.id))
    setChips(withChips([...chips, chip], brought))
    setPicked({ chip, brought })
  }

  /* Opened with only a contact (a stop card's *Text*, the Inbox's *New text*): they bring their records (§9.2). */
  useEffect(() => {
    if (prefill.contactId && (prefill.links ?? []).length === 0 && !prefill.fromConversationId) void pickContact(prefill.contactId)
  }, [])

  const send = async () => {
    if (!contact) { setProblem('Pick who it’s to.'); return }
    if (!contact.mobile) { setProblem(`No mobile number for ${contact.firstName}`); return }
    if (chips.length === 0) { setProblem('Attach it to a contact, property or job.'); return }
    if (!body.trim() && files.files.length === 0) { setProblem('Write the message.'); return }
    setBusy(true)
    setProblem(null)
    try {
      await activityRequest('/conversations', {
        method: 'POST',
        body: {
          contactId: contact.id, channel: 'sms', body: body.trim(), to: contact.mobile,
          fileIds: files.files.map((file) => file.id), links: refsOf(chips),
          ...(prefill.origin ? { origin: prefill.origin } : {}),
        },
      })
      onMade()
      onClose()
    } catch (cause) {
      setProblem(problemOf(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <NewShell title="New text" sub={setup?.sub ?? ''} look="text" width={480} phone={phone}
      note={showsOnWords(chips.map((chip) => ({ type: chip.type, label: chip.label })))}
      primary="Send" phoneAction="Send" onPrimary={() => void send()} busy={busy || files.busy}
      dirty={body.trim().length > 0 || files.files.length > 0} onClose={onClose} problem={problem ?? files.problem}>
      <Field label="To" required phone={phone}>
        <ContactField label="To" tall phone={phone} contact={contact} onPick={(id) => void pickContact(id)}
          detail={contact?.mobile ? `${contact.mobile} · Mobile` : null} />
      </Field>
      {(noMobile || stopped) && contact && (
        <div className="mt-1.5 flex items-center gap-2 text-[12px] text-warn">
          {noMobile ? `No mobile number for ${contact.firstName}` : `${contact.firstName} has asked not to be texted`}
          {contact.email && (
            <button type="button" className="font-semibold text-accent"
              onClick={() => onSwitch('email', { ...prefill, contactId: contact.id, links: chips.map((chip) => ({ type: chip.type, id: chip.id, label: chip.label })) })}>
              New email
            </button>
          )}
        </div>
      )}
      <Gap />
      <AttachedTo chips={chips} required phone={phone} hint={hint} id="new-text-attached" kinds={CONVERSATION_KINDS}
        onChange={(next, pick) => { setChips(next); setPicked(pick ?? null) }} />
      <Gap />
      <Field label="Message" phone={phone}>
        <WordsBox label="Message" value={body} onChange={setBody} minHeight={84} phone={phone}
          placeholder={contact ? `Text ${contact.firstName}…` : undefined}
          tools={(
            <>
              <FileTools onFiles={(list) => void files.add(list)} />
              <span className="flex-1" />
              <span className="text-[11.5px] text-text-faint">
                {count.parts === 1 ? '1 text' : `${count.parts} texts`} · {count.used} / {count.limit}
              </span>
            </>
          )} />
        <FileChips files={files.files} onRemove={files.remove} />
      </Field>
      <div className="mt-2.5 flex items-center gap-1.5 text-[12px] text-text-muted">
        <Glyph name="send" size={13} />
        From {setup?.smsFrom ? formatPhone(setup.smsFrom) : 'the company’s number'} · the company’s number · replies come to you
      </div>
    </NewShell>
  )
}
